import { mapValues } from 'lodash';
import getDetailedProductValidationMessage from './getDetailedProductValidationMessage';

const formatEventRegistrationsForValidationDisplay = eventRegistrations =>
  eventRegistrations.map(reg => ({
    firstName: reg.attendee.personalInformation.firstName,
    lastName: reg.attendee.personalInformation.lastName
  }));

/**
 * The outer layer for validation implementations requires three functions that answer three questions:
 * 1) Is a given object applicable to a given event registration?
 * 2) Is the validation satisfied for a given event registration?
 * 3) What message should be shown if the validation fails?
 *
 * From that, this function will create another function that accepts all information needed at
 * runtime to run validations. The result of that is an object that conforms to the nucleus form
 * validation framework.
 * @param {(object, eventRegistration) => boolean} objectValidatesRegistrant
 * @param {(object, eventRegistration) => boolean} registrantIsValid
 * @param {(translate, object, showRegistrantsInValidationMessages) => string} getValidationMessage
 */
export default
({ objectValidatesRegistrant, registrantIsValid, getValidationMessage }) =>
  /**
   * @param {function} translate
   * @param {Array} currentRegistrants
   *  Usually primary plus guests
   * @param {Object} validationObjects
   *  Object where the values represent the owner of the validation concept
   * @param {boolean} showRegistrantsInValidationMessages
   *  Whether to add the list of failing registrants to the error message
   *
   * @returns Object where the keys are the same as the ones on validationObjects, and
   * the values are nucleus form validation objects, like
   * {
   *  validator: (context) => boolean,
   *  errorMessage: (context) => string
   * }
   * errorMessage() is only invoked by the framework if validator() returns false
   */
  ({ translate, currentRegistrants, validationObjects, showRegistrantsInValidationMessages }) => {
    const getRuleBreakers = (bundle, context) => {
      const eventRegistrations = (bundle && bundle.registrations) || [];

      return eventRegistrations
        .filter(reg => objectValidatesRegistrant(bundle.object, reg, context))
        .filter(reg => !registrantIsValid(bundle.object, reg, context));
    };

    const isValid = (bundle, context) => {
      return getRuleBreakers(bundle, context).length === 0;
    };

    const getValidationMessageWithRegistrants = (bundle, context) => {
      if (isValid(bundle, context)) {
        return null;
      }

      const validationMessage = getValidationMessage(translate, bundle.object,
        showRegistrantsInValidationMessages, context);
      if (!showRegistrantsInValidationMessages) {
        return validationMessage;
      }

      const ruleBreakers = formatEventRegistrationsForValidationDisplay(getRuleBreakers(bundle, context));
      return getDetailedProductValidationMessage(translate, validationMessage, ruleBreakers);
    };

    return mapValues(validationObjects, object => {
      const bundle = {
        object,
        registrations: currentRegistrants
      };
      return {
        validator: isValid.bind(null, bundle),
        errorMessage: getValidationMessageWithRegistrants.bind(null, bundle)
      };
    });
  };
